import { useState } from "react";
import { DatePicker, ConfigProvider, theme as antdTheme } from "antd";
import dayjs, { type Dayjs } from "dayjs";
import { useTheme } from "next-themes";
import {
  Activity,
  Heart,
  Moon,
  Footprints,
  Flame,
  Watch,
  ArrowUpRight,
  ArrowDownRight,
} from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  CardDescription,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Badge } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/badge";
import { Progress } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/progress";
import { Skeleton } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/skeleton";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  AreaChart,
  Area,
  LineChart,
  Line,
  Legend,
} from "recharts";
import { useFitnessData } from "@/projects/Admin_Bite_Balance/bite-balance/src/hooks/useApi";

const metricIconMap: Record<string, React.ElementType> = {
  "avg-steps": Footprints,
  "avg-calories": Flame,
  "avg-sleep": Moon,
  "avg-rhr": Heart,
  "fitbit-connected": Watch,
  "avg-active-mins": Activity,
};

const AdminFitness = () => {
  const { resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(dayjs());

  const dateParam = selectedDate ? selectedDate.format("YYYY-MM-DD") : undefined;

  const { data: fitnessData, isLoading } = useFitnessData(dateParam);

  const tooltipStyle = {
    backgroundColor: "hsl(var(--card))",
    border: "1px solid hsl(var(--border))",
    borderRadius: "8px",
    color: "hsl(var(--foreground))",
  };

  // Map sleep_patterns to chart-friendly format
  const sleepPatterns = (fitnessData?.sleep_patterns ?? []).map(item => ({
    day: item.day,
    avgHours: item["Avg Hours"],
  }));

  // Map heart_rate_trends to chart-friendly format
  const heartRateTrends = (fitnessData?.heart_rate_trends ?? []).map(item => ({
    week: item.week,
    avgResting: item.resting,
  }));

  // Max users for HRV progress normalization
  const maxHrvUsers = Math.max(
    ...(fitnessData?.hrv_distribution ?? []).map(i => i.users),
    1
  );

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">
            Fitness & Health Overview
          </h1>
          <p className="mt-1 text-sm text-muted-foreground">
            Aggregate fitness data across all BiteBalance users
          </p>
        </div>
        <ConfigProvider
          theme={{
            algorithm: isDark ? antdTheme.darkAlgorithm : antdTheme.defaultAlgorithm,
          }}
        >
          <DatePicker
            value={selectedDate}
            onChange={val => setSelectedDate(val)}
            allowClear={false}
            className="w-36"
          />
        </ConfigProvider>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6">
        {isLoading
          ? Array.from({ length: 6 }).map((_, i) => (
              <Card key={i}>
                <CardContent className="p-4">
                  <Skeleton className="mb-2 h-8 w-8 rounded-lg" />
                  <Skeleton className="mb-1 h-6 w-16" />
                  <Skeleton className="h-3 w-24" />
                </CardContent>
              </Card>
            ))
          : (fitnessData?.metrics ?? []).map(metric => {
              const Icon = metricIconMap[metric.id] ?? Activity;
              return (
                <Card key={metric.id}>
                  <CardContent className="p-4">
                    <div className="mb-2 flex items-center gap-2">
                      <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary/10">
                        <Icon className="h-4 w-4 text-primary" />
                      </div>
                    </div>
                    <p className="text-xl font-bold text-foreground">
                      {typeof metric.value === "number"
                        ? metric.value.toLocaleString()
                        : metric.value}
                    </p>
                    <div className="mt-0.5 flex items-center gap-1">
                      <span className="text-[10px] text-muted-foreground">
                        {metric.label}
                      </span>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
      </div>

      {/* Steps Distribution */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Steps Distribution</CardTitle>
          <CardDescription>How daily steps vary across users</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="h-64">
            {isLoading ? (
              <Skeleton className="h-full w-full" />
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={fitnessData?.steps_distribution ?? []}>
                  <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                  <XAxis
                    dataKey="range"
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                  />
                  <YAxis
                    tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                  />
                  <Tooltip contentStyle={tooltipStyle} />
                  <Bar
                    dataKey="users"
                    name="Users"
                    fill="hsl(var(--primary))"
                    radius={[6, 6, 0, 0]}
                  />
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Sleep + Heart Rate */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-2">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Sleep Patterns</CardTitle>
            <CardDescription>Avg sleep duration by day of week</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              {isLoading ? (
                <Skeleton className="h-full w-full" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={sleepPatterns}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="day"
                      tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                    />
                    <YAxis
                      tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                    />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Bar
                      dataKey="avgHours"
                      name="Avg Hours"
                      fill="hsl(var(--chart-3))"
                      radius={[6, 6, 0, 0]}
                    />
                  </BarChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Heart Rate Trends</CardTitle>
            <CardDescription>Weekly avg resting HR</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              {isLoading ? (
                <Skeleton className="h-full w-full" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={heartRateTrends}>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="week"
                      tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                    />
                    <YAxis
                      tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }}
                      domain={[50, 100]}
                    />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Legend />
                    <Line
                      type="monotone"
                      dataKey="avgResting"
                      name="Resting"
                      stroke="hsl(var(--chart-2))"
                      strokeWidth={2}
                      dot={{ fill: "hsl(var(--chart-2))" }}
                    />
                  </LineChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Activity by Day + Goal Completion + HRV */}
      <div className="grid grid-cols-1 gap-4 lg:grid-cols-3">
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Activity by Day</CardTitle>
            <CardDescription>Average steps per day this week</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="h-56">
              {isLoading ? (
                <Skeleton className="h-full w-full" />
              ) : (
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart 
                    data={fitnessData?.activity_by_day ?? []}
                    margin={{ top: 5, right: 20, left: 0, bottom: 5 }}
                  >
                    <defs>
                      <linearGradient id="activityHourGrad" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="hsl(var(--primary))" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="hsl(var(--primary))" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                    <XAxis
                      dataKey="day"
                      tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 10 }}
                      interval={0}
                    />
                    <YAxis tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} />
                    <Tooltip contentStyle={tooltipStyle} />
                    <Area
                      type="monotone"
                      dataKey="Avg Steps"
                      name="Avg Steps"
                      stroke="hsl(var(--primary))"
                      fill="url(#activityHourGrad)"
                      strokeWidth={2}
                    />
                  </AreaChart>
                </ResponsiveContainer>
              )}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Goal Completion Rates</CardTitle>
            <CardDescription>% of users hitting daily goals</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-4">
                {Array.from({ length: 3 }).map((_, i) => (
                  <Skeleton key={i} className="h-6 w-full" />
                ))}
              </div>
            ) : (
              <div className="space-y-4">
                {(fitnessData?.goal_completion_rates ?? []).map(item => (
                  <div key={item.id} className="space-y-1.5">
                    <div className="flex items-center justify-between text-xs">
                      <span className="text-muted-foreground">{item.label}</span>
                      <span className="font-bold text-foreground">
                        {item.percentage.toFixed(1)}%
                      </span>
                    </div>
                    <Progress value={item.percentage} className="h-1.5" />
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base">HRV Distribution</CardTitle>
            <CardDescription>Heart rate variability across users</CardDescription>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="space-y-3">
                {Array.from({ length: 6 }).map((_, i) => (
                  <Skeleton key={i} className="h-5 w-full" />
                ))}
              </div>
            ) : (
              <div className="space-y-3">
                {(fitnessData?.hrv_distribution ?? []).map(item => (
                  <div key={item.range} className="flex items-center gap-3">
                    <span className="w-16 text-xs text-muted-foreground">
                      {item.range}
                    </span>
                    <div className="flex-1">
                      <Progress
                        value={(item.users / maxHrvUsers) * 100}
                        className="h-2"
                      />
                    </div>
                    <span className="w-10 text-right text-xs font-semibold text-foreground">
                      {item.users}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminFitness;
